<template>
    <b-modal :active.sync="isActive" :width="640" scroll="keep">
        <div class="card" style="width: auto">
            <header class="modal-card-head">
                <p class="modal-card-title">Betaalmethode wijzigen</p>
            </header>
            <section class="modal-card-body">
                <FullPageLoader :isLoading="loading"/>
                <template v-if="!loading">
                    <p>
                        Huidige betaalmethode: <strong>{{ contract.step1.payment_type === 'incasso' ? 'Automatische Incasso' : 'Handmatig' }}</strong>.
                    </p>
                    <p>
                        Selecteer de nieuwe betaalmethode voor dit contract. Alle openstaande en toekomstige facturen zullen via de nieuwe methode worden verwerkt.
                    </p>

                    <br>

                    <b-field label="Nieuwe betaalmethode">
                        <b-select v-model="newPaymentType" placeholder="Selecteer een betaalmethode" expanded>
                            <option value="incasso" :disabled="contract.step1.payment_type === 'incasso'">
                                Automatische Incasso {{ contract.step1.payment_type === 'incasso' ? ' (huidige betaalmethode)' : '' }}
                            </option>
                            <option value="manual" :disabled="contract.step1.payment_type === 'manual'">
                                Handmatig {{ contract.step1.payment_type === 'manual' ? ' (huidige betaalmethode)' : '' }}
                            </option>
                        </b-select>
                    </b-field>

                    <div v-if="message" class="notification is-warning">
                        {{ message }}
                    </div>

                    <div v-if="bankDetailsError" class="notification is-danger">
                        <div class="content">
                            <strong>IBAN-gegevens ontbreken</strong>
                            <p class="marginbottom0">
                                De klant moet eerst IBAN-gegevens (rekeninghouder en bankrekeningnummer) hebben voordat automatische incasso kan worden ingeschakeld.&nbsp;
                                <router-link :to="{ name: 'CustomersRecord', params: { id: contract.step2.customer_id } }" type="is-primary">
                                    Klant bewerken
                                </router-link>
                            </p>
                        </div>
                    </div>

                    <div v-if="allAffectedInvoices.length > 0">
                        <h3 class="title is-5 m-t-15">Beïnvloede facturen</h3>
                        <p>De status van de volgende openstaande facturen:</p>
                        <ul class="margin0">
                            <li v-for="invoice in allAffectedInvoices" :key="invoice.id" class="is-flex is-justify-content-space-between is-align-items-center p-b-5">
                                <span>Factuur #{{ invoice.number }} - &euro;{{ invoice.total }}</span>
                                <b-tag :type="invoice.isChangeable ? 'is-success' : 'is-danger'">
                                    {{ invoice.isChangeable ? 'Wijziging mogelijk' : 'Incasso al aangemeld' }}
                                </b-tag>
                            </li>
                        </ul>
                    </div>

                    <div v-if="allAffectedInvoices.length === 0 && newPaymentType && !bankDetailsError">
                        <p>Er zijn geen openstaande facturen die beïnvloed worden.</p>
                    </div>
                </template>
            </section>
            <footer class="modal-card-foot">
                <b-button @click="$emit('close')">Annuleren</b-button>
                <b-button type="is-primary" @click="changePaymentType" :disabled="loading || !newPaymentType || newPaymentType === contract.step1.payment_type || !unpaidInvoices.length || !!bankDetailsError">
                    Wijzigingen
                </b-button>
            </footer>
        </div>
    </b-modal>
</template>

<script>
import toastMixins from '@/mixins/toastMixins.js'

export default {
    name: 'ChangePaymentTypeModal',
    mixins: [toastMixins],
    props: {
        show: {
            type: Boolean,
            default: false
        },
        contract: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            loading: false,
            unpaidInvoices: [],
            blockedInvoices: [],
            message: '',
            bankDetailsError: '',
            newPaymentType: null
        }
    },
    computed: {
        isActive: {
            get() {
                return this.show;
            },
            set(value) {
                this.$emit('close');
            }
        },
        allAffectedInvoices() {
            const changeable = this.unpaidInvoices.map(invoice => ({ ...invoice, isChangeable: true }));
            const blocked = this.blockedInvoices.map(invoice => ({ ...invoice, isChangeable: false }));
            return [...changeable, ...blocked].sort((a, b) => a.number.localeCompare(b.number));
        }
    },
    methods: {
        precheck() {
            if (!this.newPaymentType || this.newPaymentType === this.contract.step1.payment_type) {
                this.unpaidInvoices = [];
                this.blockedInvoices = [];
                this.message = this.newPaymentType ? 'Dit is al de huidige betaalmethode.' : 'Selecteer een betaalmethode.';
                this.bankDetailsError = '';
                return;
            }
            this.loading = true;
            this.bankDetailsError = '';
            this.message = '';

            axios.post(`/api/contract/${this.$route.params.id}/precheck-change-payment-type`, {
                payment_type: this.newPaymentType
            }, {
                headers: {'Authorization': 'bearer ' + this.$store.state.authToken}
            }).then(response => {
                const data = response.data;
                this.unpaidInvoices = data.unpaid_invoices;
                this.blockedInvoices = data.blocked_invoices;
                this.message = data.message;
            }).catch(error => {
                if (error.response && error.response.status === 422 && error.response.data.error) {
                    // Bank details validation error
                    this.bankDetailsError = error.response.data.error;
                    this.unpaidInvoices = [];
                    this.blockedInvoices = [];
                    this.message = '';
                } else {
                    this.showError('Fout bij het controleren van de betaalmethode.');
                }
            }).finally(() => {
                this.loading = false;
            });
        },
        changePaymentType() {
            this.loading = true;
            axios.post(`/api/contract/${this.$route.params.id}/change-payment-type`, {
                payment_type: this.newPaymentType
            }, {
                headers: {'Authorization': 'bearer ' + this.$store.state.authToken}
            }).then(response => {
                this.showMessage('Betaalmethode succesvol gewijzigd.');
                this.$emit('success');
                this.$emit('close');
            }).catch(error => {
                this.showError(error.response.data.error || 'Fout bij het wijzigen van de betaalmethode.');
            }).finally(() => {
                this.loading = false;
            });
        },
        reset() {
            this.loading = false;
            this.unpaidInvoices = [];
            this.blockedInvoices = [];
            this.message = '';
            this.bankDetailsError = '';
            this.newPaymentType = null;
        }
    },
    watch: {
        isActive(newVal) {
            if (newVal) {
                this.reset();
            }
        },
        newPaymentType() {
            if (this.isActive) {
                this.precheck();
            }
        }
    }
}
</script>
